import { extend } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Model from 'flarum/common/Model';
import Discussion from 'flarum/common/models/Discussion';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionHero from 'flarum/forum/components/DiscussionHero';
import SpeciesLabel from './components/SpeciesLabel';
import SpeciesAssignModal from './components/SpeciesAssignModal';
import { hasIdentificationTag, reminderDelayElapsed } from './utils/taxonGroups';
// gbifId 0 is the "Species incognita" sentinel (see SpeciesLookupService) -
// /species/0 is a real, working page for it, so the reminder-free label
// below can link there exactly like a real species would.
const UNDETERMINED_PSEUDO_SPECIES = {
  gbifId: 0,
  canonicalName: 'Species incognita'
};
export default function addSpeciesLabels() {
  // The `species` field's vernacularName (rendered by SpeciesLabel.js
  // below) is resolved server-side, opting in automatically for any
  // single-discussion view - see extend.php's own `$context->showing()`
  // check on that field. Nothing to request from here: unlike
  // DescriptionsPage.js's own list, which has to opt in per-request with
  // ?vernacularName=true (a real cost concern at ~230 rows), a single
  // discussion page is cheap enough to just always include it, and doing
  // that server-side covers both the ways this page loads - a fresh/
  // bookmarked link (core's own server-side prerender,
  // Forum\Content\Discussion) and in-app navigation (DiscussionPage.tsx's
  // own client-side re-fetch) - without needing a request param only one
  // of those two paths would remember to send.

  // Discussion.species() -> [{gbifId, canonicalName, scientificName, author,
  // synonymCanonicalName, synonymAuthor}, ...], already resolved server-side
  // (see the `species` field on DiscussionResource in extend.php).
  Object.defineProperty(Discussion.prototype, 'species', {
    value: Model.attribute('species')
  });

  // Discussion.speciesAssignmentMode() -> 'species'|'undetermined'|'no-species'|null,
  // read by SpeciesAssignModal to pre-select the right mode when editing.
  Object.defineProperty(Discussion.prototype, 'speciesAssignmentMode', {
    value: Model.attribute('speciesAssignmentMode')
  });

  // Discussion.canAssignSpecies() -> bool, true only for the discussion's
  // author, moderators, or admins (mirrors the same check in the
  // species field's set() on the server) - gates the "Espèce" control.
  Object.defineProperty(Discussion.prototype, 'canAssignSpecies', {
    value: Model.attribute('canAssignSpecies')
  });

  // Discussion.descriptionImageUrl() -> string|null, only populated when
  // the request opted in with ?images=true (see DescriptionsPage.js) -
  // first image of the discussion's own first post, same thumbnail as the
  // homepage's RandomDescriptions widget.
  Object.defineProperty(Discussion.prototype, 'descriptionImageUrl', {
    value: Model.attribute('descriptionImageUrl')
  });

  // Next to each discussion in a discussion list.
  extend(DiscussionListItem.prototype, 'infoItems', function (items) {
    const discussion = this.attrs.discussion;
    const species = discussion.species();
    if (species && species.length) {
      items.add('species', m("span", {
        className: "DiscussionListItem-species"
      }, species.map(s => m(SpeciesLabel, {
        key: s.gbifId,
        species: s
      }))), 10);
    } else if (discussion.speciesAssignmentMode() === 'undetermined') {
      items.add('species', m("span", {
        className: "DiscussionListItem-species"
      }, undeterminedSpeciesLabel()), 10);
    }
    if (isReminderNeeded(discussion)) {
      items.add('speciesReminder', m("p", {
        className: "ChampisSpeciesReminder"
      }, m("a", {
        onclick: e => {
          e.stopPropagation();
          e.preventDefault();
          openSpeciesModal(discussion);
        }
      }, app.translator.trans('champis-species.forum.assign_species.author_reminder'))), 20);
    }
  });

  // In the discussion page's hero, next to the title (visible while reading
  // its posts) - shows the species link(s) if any, plus an "Ajouter une
  // espèce"/"Modifier" affordance right there for anyone allowed to change
  // it (rather than only via the "..." controls dropdown in the sidebar,
  // which is easy to miss).
  extend(DiscussionHero.prototype, 'items', function (items) {
    const discussion = this.attrs.discussion;
    const species = discussion.species();
    const canAssign = discussion.canAssignSpecies && discussion.canAssignSpecies();
    const relevant = hasIdentificationTag(discussion.tags ? discussion.tags() : []);
    const isUndetermined = !(species && species.length) && discussion.speciesAssignmentMode() === 'undetermined';
    const openModal = () => openSpeciesModal(discussion);
    if (isReminderNeeded(discussion)) {
      items.add('speciesReminder', m("p", {
        className: "ChampisSpeciesReminder"
      }, m("a", {
        onclick: openModal
      }, app.translator.trans('champis-species.forum.assign_species.author_reminder'))), 20);
    }
    if (!(species && species.length) && !isUndetermined && !(canAssign && relevant)) return;
    items.add('species', m("ul", {
      className: "DiscussionHero-species"
    }, species.map(s => m("li", {
      key: s.gbifId
    }, m(SpeciesLabel, {
      species: s
    }))), isUndetermined && m("li", null, undeterminedSpeciesLabel()), canAssign && relevant && m("li", null, m("button", {
      type: "button",
      className: "DiscussionHero-speciesEdit",
      onclick: openModal
    }, m("i", {
      className: "icon fas fa-dna",
      "aria-hidden": "true"
    }), ' ', app.translator.trans(species.length ? 'champis-species.forum.assign_species.edit_link' : 'champis-species.forum.assign_species.add_link')))),
    // Below flarum-tags' own 'tags' item (priority 5) and the title
    // (default priority 0), so the hero reads: reminder, tags, title,
    // species - one line each, per the requested order.
    -1);
  });
}

// Red reminder for the discussion's own author specifically (not just
// anyone who *can* assign a species - mods/admins don't need nagging about
// someone else's post) when nothing has been decided yet at all (not even
// "non déterminé"/"sans espèce", which are real decisions).
function isReminderNeeded(discussion) {
  const author = discussion.user && discussion.user();
  const isAuthor = !!(app.session.user && author && author.id() === app.session.user.id());
  const tags = discussion.tags ? discussion.tags() : [];
  const relevant = hasIdentificationTag(tags);
  return isAuthor && relevant && !discussion.speciesAssignmentMode() && reminderDelayElapsed(discussion, tags);
}

// Shown wherever a real species link normally would be, so an explicit
// "Non déterminé" decision reads as a decision (and links to its own
// /species/0 page listing every other undetermined discussion) rather
// than looking identical to "nobody has assigned anything yet" (which
// shows nothing at all).
function undeterminedSpeciesLabel() {
  return m(SpeciesLabel, {
    species: UNDETERMINED_PSEUDO_SPECIES
  });
}
function openSpeciesModal(discussion) {
  app.modal.show(SpeciesAssignModal, {
    discussion,
    onsubmit: entries => discussion.save({
      species: entries
    })
  });
}